import { Flex, Text } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import BaseClickable from '@/components/ui/BaseClickable';
import Loader from '@/components/ui/Loader';
import { ROUTES } from '@/configs/routes';
import { getDocumentAnalysisIcon } from '@/entities/DocumentAnalysis';
import EmptyStubContent from '@/pages/EmptyStubContent';
import { analysesStore } from '@/stores/global/RootStore';

import s from './MacroContent.module.scss';

type MacroValue = {
  id: number;
  hasResult: boolean;
};

const MacroContent: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const analysesMeta = id ? analysesStore.getMeta(id, null) : null;
  const isLoadingAnalyses = Boolean(analysesMeta?.isLoading);

  const analysesItemsByScope = id ? analysesStore.getAvailableAnalyses(id, null) : { DOCUMENT: [], ORGANIZATION: [] };
  const hasAvailableOrganizationResults = id ? analysesStore.hasAvailableResult(id, null) : false;

  const handleSelect = React.useCallback(
    (value: MacroValue) => {
      if (!id || !value.hasResult) {
        return;
      }
      navigate(ROUTES.organization.macro.analyze.result.create(id, value.id));
    },
    [id, navigate],
  );

  const organizationAnalyses = analysesItemsByScope.ORGANIZATION;
  const hasOrganizationAnalyses = organizationAnalyses.length > 0;

  if (!id) {
    return <EmptyStubContent title="Макро анализ недоступен" text="Нет доступных макро-анализов для организации." />;
  }

  if (isLoadingAnalyses && !hasOrganizationAnalyses) {
    return <Loader gap={0} fullHeight type="dots" size="md" text="Загружаю доступные анализы..." />;
  }

  if (!hasOrganizationAnalyses || !hasAvailableOrganizationResults) {
    return <EmptyStubContent title="Макро анализ недоступен" text="Нет доступных макро-анализов для организации." />;
  }

  return (
    <Flex className={s.root} direction="column" gap="sm">
      {organizationAnalyses.map((item) => {
        const hasResult = item.analysis_result_id !== null;

        if (!hasResult) {
          return null;
        }

        return (
          <BaseClickable
            key={item.id}
            block
            disabled={!hasResult}
            value={{ id: item.id, hasResult }}
            onClickWithValue={handleSelect}
            className={s.root__item}
          >
            <>
              <Flex align="center" gap="sm" mb="xs">
                <div className={s['root__icon-wrap']}>{getDocumentAnalysisIcon(item.id)}</div>
                <Text fw={600}>{item.name}</Text>
              </Flex>
              <Text size="sm" c="dimmed">
                {item.description}
              </Text>
              <Flex className={s['root__action-row']} align="center" gap={4}>
                <Text size="sm">Смотреть</Text>
                <IconChevronRight size={16} />
              </Flex>
            </>
          </BaseClickable>
        );
      })}
    </Flex>
  );
};

export default observer(MacroContent);
